import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";

import { ProcessingSpinner } from "@/components/processing-spinner";
import { useRevisionRetry } from "@/components/projects/revision-retry";
import { RunStatusBadge } from "@/components/run-status-badge";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";

export interface JobViewerSource {
  sourceId: string;
  sourceType: "plan" | "schedule";
  fileUrl: string | null;
  displayName?: string;
  filename: string;
  documentOrder: number;
  revisionId: Id<"projectDocumentRevisions">;
  /** The packet build state from the viewer-source query: still building, built, or failed. */
  fileStatus?: "pending" | "ready" | "failed";
  fileError?: string | null;
}

/** Plan sets first in document order, then schedules: the order job viewers list sources in. */
export function compareViewerSources(
  a: Pick<JobViewerSource, "sourceType" | "documentOrder">,
  b: Pick<JobViewerSource, "sourceType" | "documentOrder">
) {
  return Number(a.sourceType === "schedule") - Number(b.sourceType === "schedule") || a.documentOrder - b.documentOrder;
}

/**
 * The document a job viewer opens by default: its first plan set, even while
 * that plan's selected pages are still being prepared, so a schedule that
 * finishes first never takes its place. Without a plan, the first ready source.
 */
export function defaultViewerSource<T extends JobViewerSource>(sources: readonly T[]): T | undefined {
  const ordered = [...sources].sort(compareViewerSources);
  return ordered.find((source) => source.sourceType === "plan") ?? ordered.find((source) => source.fileUrl);
}

/**
 * The source a job viewer shows: the user's choice while it still exists, even
 * if it is still being prepared, otherwise the default.
 */
export function resolveActiveViewerSource<T extends JobViewerSource>(
  sources: readonly T[],
  selectedSourceId: string | null | undefined
): T | undefined {
  return sources.find((source) => source.sourceId === selectedSourceId) ?? defaultViewerSource(sources);
}

function preparationFailed(source: JobViewerSource) {
  return !source.fileUrl && source.fileStatus === "failed";
}

/**
 * Shown while a job's documents are prepared from selected pages. Each
 * document reports Ready, Preparing (automatic retries included) or Failed with
 * the reason; ready ones open right away and editors can retry failed ones.
 */
export function PreparingDocumentsState({
  sources,
  onOpen,
  canRetry = false,
}: {
  sources: readonly JobViewerSource[];
  onOpen?: (sourceId: string) => void;
  /** Editors can rebuild a failed document. */
  canRetry?: boolean;
}) {
  const { retryRevision, isRetrying } = useRevisionRetry();
  const ordered = [...sources].sort(compareViewerSources);
  const isPreparing = ordered.some((source) => !source.fileUrl && !preparationFailed(source));
  const preparingMessage = ordered.some((source) => source.sourceType === "plan")
    ? "Preparing your selected pages. The plan set opens as soon as it's ready."
    : "Preparing your selected pages. They open as soon as they're ready.";
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 p-6">
      {isPreparing ? (
        <ProcessingSpinner className="py-6" message={preparingMessage} />
      ) : (
        <p className="py-6 text-center text-sm font-medium text-muted-foreground" role="status">
          {canRetry
            ? "Some selected pages couldn't be prepared. Retry to prepare them again."
            : "Some selected pages couldn't be prepared. Ask an editor to retry them."}
        </p>
      )}
      <ul className="w-full max-w-md divide-y divide-border rounded-lg border-2 border-border bg-card">
        {ordered.map((source) => (
          <li key={source.sourceId} className="flex items-center justify-between gap-3 px-4 py-2.5">
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium">{source.displayName || source.filename}</span>
              {preparationFailed(source) && source.fileError ? (
                <span className="block text-xs text-destructive">{source.fileError}</span>
              ) : null}
            </span>
            <span className="flex shrink-0 items-center gap-2">
              {source.fileUrl && onOpen ? (
                <Button type="button" variant="ghost" size="xs" onClick={() => onOpen(source.sourceId)}>
                  Open
                </Button>
              ) : null}
              {source.fileUrl ? (
                <StatusBadge status="completed" label="Ready" />
              ) : preparationFailed(source) ? (
                <>
                  {canRetry ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="xs"
                      disabled={isRetrying(source.revisionId)}
                      onClick={() => void retryRevision(source.revisionId)}
                    >
                      Retry
                    </Button>
                  ) : null}
                  <RunStatusBadge status="failed" />
                </>
              ) : (
                <StatusBadge status="processing" label="Preparing" />
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
